import { useMemo } from 'react';
import { cleanDeviceLabel } from '../util/clean_device_labels';
import appLogger from '../util/logging/app_logger';

export interface DeviceListItem {
    deviceId: string;
    label: string;
    isSelected: boolean;
    kind: MediaDeviceKind;
    displayLabel: string;
}

type DeviceAccumulator = {
    selectedDeviceInfo: DeviceListItem | null;
    deviceList: DeviceListItem[];
};

/**
 * Utility hook that returns a list of available devices with some handy features:
 * 1. Sets preffered device to first available device if applicable (either the user did not
 * select a device, or a previously selected device is no longer available).
 * 2. Maps the device list to a format more suitable for consumption.
 */
export const useDeviceList = (
    mediaDevices: MediaDeviceInfo[],
    getPreferredDeviceId: () => string | null,
    setPreferredIdFn: (id: string, label?: string) => void,
): DeviceAccumulator => {
    return useMemo(() => {
        const preferredDeviceId = getPreferredDeviceId();
        const devices = mediaDevices.reduce<DeviceAccumulator>(
            (acc, cv) => {
                const isSelected = cv.deviceId === preferredDeviceId;
                const device: DeviceListItem = {
                    deviceId: cv.deviceId,
                    displayLabel: cleanDeviceLabel(cv.label),
                    label: cv.label,
                    isSelected,
                    kind: cv.kind,
                };
                if (isSelected) {
                    acc.selectedDeviceInfo = device;
                }
                acc.deviceList.push(device);
                return acc;
            },
            {
                selectedDeviceInfo: null,
                deviceList: [],
            },
        );

        if (devices.selectedDeviceInfo === null && devices.deviceList.length > 0) {
            const firstAvailableDevice = devices.deviceList[0];
            appLogger.info(
                'No preffered device found, using first available device as preffered device:',
                firstAvailableDevice,
            );

            setPreferredIdFn(firstAvailableDevice.deviceId, firstAvailableDevice.label);

            const defaultDevice = {
                deviceId: firstAvailableDevice.deviceId,
                label: firstAvailableDevice.label,
                displayLabel: cleanDeviceLabel(firstAvailableDevice.label),
                isSelected: true,
                kind: firstAvailableDevice.kind,
            };

            devices.selectedDeviceInfo = defaultDevice;
            devices.deviceList.splice(0, 1, defaultDevice);
        }
        return devices;
    }, [getPreferredDeviceId, mediaDevices, setPreferredIdFn]);
};
